/**
 * Home health demo — the tenant's schedule for a demo day.
 *
 * Pure: who the patients are, the clinicians' shifts and blocks, and where every
 * visit lands, for one `today` and one generation time. The SQL generator writes
 * it to the tenant; nothing here touches the file system or the console, so the
 * store can build the same schedule and agree with the tenant by construction.
 */
import { demoVisitStatus } from "../../demoHelper";
import { addDaysToKey, addWorkingDaysToKey, dateKeyAt, dateKeyRange, daysBetweenKeys, isoWeekday, parseDays, socDateBefore, weekStartKey } from "../engine/clock";
import { expandEpisode } from "../engine/planExpansion";
import { estimateDriveMinutes, supplyTerritoriesFor, territoryForZip } from "../engine/territory";
import { CAST, isUnadmitted, type CastMember, type PinnedVisit } from "../fixtures/cast";
import { DANA_WEEK, MARCUS_NEXT_DAY_SOC } from "../fixtures/danaWeek";
import { DRIVE_MINUTES } from "../fixtures/driveMinutes";
import { DEFAULT_CONFIG, DEFAULT_VISIT_TYPES, DOCUMENTATION_BLOCK, FIRST_VISIT_AT, TRAIT_OASIS, TRAIT_WOUND_VAC } from "../fixtures/config";
import { FILLER_LAST_NAMES, firstNamesFor } from "../fixtures/names";
import { ADMITTED_MONDAY_WELCOME_CALL, PATIENT_SLOTS, RETIRED_PATIENT_REFS, type PatientFixture } from "../fixtures/patients";
import { ROSTER_FIXTURE } from "../fixtures/roster";
import { UNUSED_ADDRESSES_BY_TERRITORY } from "../fixtures/seedAddresses";
import { STANDING_PATTERNS } from "../fixtures/standingPatterns";
import type { DateKey, Discipline, FrequencyOrder, PatientConstraint, PlanVisit, VisitTypeKind } from "../state";

const TZ = DEFAULT_CONFIG.branch.timeZone;
export const HORIZON_BEFORE = 7;
/** Visits: the window the script's episodes are placed in. */
export const HORIZON_AFTER = 28;
/**
 * Shifts and blocks reach further than visits, because the product's own
 * generator does: `getRRuleGenerationDays` (apps/service/helpers/rrule.ts) runs
 * the nightly worker out to the largest `daysBeforeLastOption` of any visit type
 * the provider serves, so shifts exist as far ahead as a visit can be scheduled.
 * Materializing only four weeks made the roster grid invent a state the product
 * does not have — a greyed "standing pattern, not yet published" — which Pete
 * called on 2026-09-20: *"feels like something we invented… if we just made the
 * max scheduling limit on visits to be out further there would be real shifts
 * there."* Thirteen weeks covers the grid's longest span, so every cell it draws
 * is a real shift. Keep this equal to the routine `daysBeforeLastOption` below.
 */
export const SHIFT_HORIZON_AFTER = 91;
/** Visits the script pins by hand reach further back, so a Thursday patient has two same-time visits behind her (the continuity source). */
export const PIN_HORIZON_BEFORE = 21;
export interface Rule {
  handle: string;
  type: "available" | "unavailable" | "calendar_block";
  note: string;
  start: string; // HH:mm:ss local
  end: string;
  freq: "daily" | "weekly";
  dtstart: DateKey;
  until: DateKey | null;
  byweekday: number[];
  overflow?: boolean;
  shiftCenter?: boolean;
  blockType?: "break" | "meeting" | "facility" | "visit_type";
}

export interface SeedShift {
  handle: string;
  day: DateKey;
  startIso: string;
  endIso: string;
  overflow: boolean;
  shiftCenter: boolean;
}

export interface SeedBlock {
  handle: string;
  day: DateKey;
  note: string;
  startIso: string;
  endIso: string;
  blockType: string;
}

export interface SeedPatient extends PatientFixture {
  line2?: string;
  mrn: string;
  socDate?: DateKey;
  team: CastMember["team"];
  requiredTraits: string[];
  stage?: CastMember["stage"];
  /** Orders the cast states for this patient; they win over the case type's starter defaults. */
  orders?: FrequencyOrder[];
  /** Real visits only up to this date (later plan visits stay store placeholders). */
  realThrough?: DateKey;
  /** Authorized real visits per discipline; anything past the cap is provisional and never reaches the tenant. */
  realVisitCap?: Partial<Record<Discipline, number>>;
  /** Visits the script places by day and time. */
  pinnedVisits?: PinnedVisit[];
}

export interface SeedVisit {
  patient: SeedPatient;
  handle: string;
  kind: VisitTypeKind;
  day: DateKey;
  startIso: string;
  endIso: string;
  startDrive: number;
  interDrive: number;
  endDrive: number;
  /** As of the demo time, by the rule Copy demo data uses (`demoVisitStatus`); set once the drives are final. */
  status: ReturnType<typeof demoVisitStatus>;
  /** The plan visit this visit delivers (`expandEpisode`'s id), when it delivers one; the store dates that plan visit here. */
  planVisitId?: string;
}

/** Case mix for generated census: nursing-heavy, therapy lighter (10 slots). */
const CENSUS_MIX = ["chfSn", "chfSn", "woundCareSnPt", "chfSn", "orthoPtOt", "chfSn", "hhaSupport", "strokeSnPtOtSlp", "woundCareSnPt", "chfSn"];

/**
 * Territory mix for the generated census (20 slots): North stays deliberately
 * heavy because Dana's week is the one on screen, but every territory carries
 * enough work for its owner to read as a real caseload.
 */
const TERRITORY_MIX = ["North", "North", "East", "South", "North", "West", "North", "East", "South", "North", "East", "North", "South", "West", "North", "East", "North", "South", "East", "West"];

/**
 * Who a discipline's work in a territory goes to: the owner first, then whoever
 * covers that territory, then the only clinician of that discipline on the roster.
 * Assignment follows territory ownership.
 */
export function ownerFor(discipline: Discipline, territory: string, requiredTraits: string[] = []): string | undefined {
  const pool = ROSTER_FIXTURE.filter((e) => e.discipline === discipline && requiredTraits.every((t) => e.traits.includes(t)));
  return (pool.find((e) => e.territories.includes(territory)) ?? pool.find((e) => e.coversTerritories?.includes(territory)) ?? pool[0])?.handle;
}

function teamFor(zip: string, caseType: string, i: number): CastMember["team"] {
  const territory = territoryForZip(DEFAULT_CONFIG.branch, zip) ?? "East";
  const traits = caseType === "woundCareSnPt" ? [TRAIT_WOUND_VAC] : [];
  // Assessments go to an OASIS-competent RN; South's owner carries no OASIS trait, so they fall to the line-wide assessor.
  const assessor = ownerFor("RN", territory, [TRAIT_OASIS, ...traits]);
  const team: CastMember["team"] = { RN: ownerFor("RN", territory, traits) ?? assessor, LPN: ownerFor("LPN", territory), assessor };
  if (caseType !== "chfSn" && caseType !== "hhaSupport") {
    team.PT = ownerFor("PT", territory);
    team.PTA = i % 2 ? ownerFor("PTA", territory) : undefined;
  }
  if (caseType === "orthoPtOt" || caseType === "strokeSnPtOtSlp") team.OT = ownerFor("OT", territory);
  if (caseType === "strokeSnPtOtSlp") team.SLP = ownerFor("SLP", territory);
  if (caseType === "hhaSupport") team.HHA = ownerFor("HHA", territory);
  team.MSW = ownerFor("MSW", territory);
  return team;
}

/** Floor for a drive between two visits in the same territory; the product recomputes real drives on every move. */
const MIN_DRIVE = 12;

/**
 * The drive the seed writes for a leg: the real minutes harvested for it, in
 * either direction (`fixtures/driveMinutes.ts`), else the estimate. Each end is a
 * patient's ref or a clinician's home (`@dana`).
 */
function drive(from: string, to: string, estimate: number): number {
  return DRIVE_MINUTES[`${from}>${to}`] ?? DRIVE_MINUTES[`${to}>${from}`] ?? estimate;
}

/** Which roster handle takes a plan visit for this patient. */
function assignee(p: SeedPatient, v: PlanVisit, today: DateKey): string | undefined {
  if (v.visitTypeKind === "SOC" || v.visitTypeKind === "ROC" || v.visitTypeKind === "RECERT") return p.team.assessor ?? p.team.RN;
  if (v.visitTypeKind === "RN_SUPERVISORY") return p.team.RN;
  switch (v.discipline) {
    case "RN": {
      // Wound VAC stays with the RN; otherwise alternate the LPN in, when the LPN ever works a day the patient can be seen.
      if (p.requiredTraits.includes(TRAIT_WOUND_VAC) || !p.team.LPN || !worksABookableDay(p.team.LPN, p.ref, today)) return p.team.RN;
      return v.weekIndex % 2 === 0 ? p.team.RN : p.team.LPN;
    }
    case "PT":
      return v.visitTypeKind === "PT_EVAL" || !p.team.PTA || !worksABookableDay(p.team.PTA, p.ref, today) ? p.team.PT : v.weekIndex % 2 ? p.team.PTA : p.team.PT;
    case "OT":
      return p.team.OT;
    case "SLP":
      return p.team.SLP;
    case "MSW":
      return p.team.MSW;
    case "HHA":
      return p.team.HHA;
    default:
      return undefined;
  }
}

/** Where a clinician's supply sits: their owned territory, or their home zip for a per diem who owns none. */
export function supplyTerritoryOf(handle: string): string {
  const e = ROSTER_FIXTURE.find((x) => x.handle === handle)!;
  return supplyTerritoriesFor({ territories: e.territories, homeZip: e.homeZip, territory: "Unassigned" }, DEFAULT_CONFIG.branch)[0];
}

export function territoryOf(p: SeedPatient): string {
  return territoryForZip(DEFAULT_CONFIG.branch, p.zip) ?? "East";
}

/** The discipline a pinned or hand-placed visit belongs to. */
function disciplineOf(kind: VisitTypeKind): Discipline {
  return DEFAULT_VISIT_TYPES[kind].disciplines[0];
}

export function ordersFor(p: SeedPatient): FrequencyOrder[] {
  const base = p.orders ?? DEFAULT_CONFIG.starterFrequencies[p.caseType] ?? [];
  return base.map((o) => ({ ...o, requiredTraits: o.discipline === "RN" ? p.requiredTraits : o.requiredTraits }));
}

/**
 * **Availability the branch knew when booking these patients** — their welcome
 * calls — so the seed books around it and their plans read clean: no seeded
 * visit sits on a day a patient said no to, or outside the window they gave
 * (Pete, 2026-10-12). The admitted-Monday patient's morning window came from his
 * welcome call; the weekday he is not available Dana learned at the door after
 * booking, and the seed picks it from the days his visits left free
 * (`doorLearnedWeekday`).
 */
export function availabilityKnownAtBooking(ref: string, today: DateKey): PatientConstraint[] | undefined {
  void today;
  if (ref === "census-01") return ADMITTED_MONDAY_WELCOME_CALL;
  return undefined;
}

/** The arrival window the seed writes around every visit's start (`seedSql.ts`): a quarter-hour before, three quarters after. */
export const ARRIVAL_WINDOW_BEFORE_MINUTES = 15;
export const ARRIVAL_WINDOW_AFTER_MINUTES = 45;

/** The patient's own hours as the branch knew them when booking, in minutes of the day; undefined when they gave none. */
function bookableWindow(ref: string, today: DateKey): [number, number] | undefined {
  const value = availabilityKnownAtBooking(ref, today)?.find((c) => c.kind === "availabilityWindow")?.value;
  const m = value?.match(/^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/);
  return m ? [Number(m[1]) * 60 + Number(m[2]), Number(m[3]) * 60 + Number(m[4])] : undefined;
}

/** Whether a visit starting at `start` on `day` has its whole arrival window inside the patient's hours. */
function insidePatientWindow(ref: string, today: DateKey, day: DateKey, start: Date): boolean {
  const window = bookableWindow(ref, today);
  if (!window) return true;
  const minute = (start.getTime() - dateKeyAt(day, "00:00", TZ).getTime()) / 60_000;
  return minute - ARRIVAL_WINDOW_BEFORE_MINUTES >= window[0] && minute + ARRIVAL_WINDOW_AFTER_MINUTES <= window[1];
}

/**
 * **The weekday Dana learned at the door that R. Whitfield is not available**: the
 * first weekday, Monday to Friday, that none of his seeded visits falls on. So no
 * seeded visit conflicts with it, and next week's is the empty day the Reserve
 * amber drop lands on, on any demo weekday.
 */
export function doorLearnedWeekday(visits: Pick<SeedVisit, "patient" | "day">[]): number {
  const taken = new Set(visits.filter((v) => v.patient.ref === "census-01").map((v) => isoWeekday(v.day)));
  const free = [1, 2, 3, 4, 5].find((d) => !taken.has(d));
  if (free === undefined) throw new Error("R. Whitfield has a visit on every weekday, so the seed leaves him no day to be unavailable on (SEED-SCOPE 2.1)");
  return free;
}

/** The ISO weekdays a patient can be seen, as the branch knew when booking; undefined when nothing rules a day out. */
export function bookableWeekdays(ref: string, today: DateKey): number[] | undefined {
  const lists = (availabilityKnownAtBooking(ref, today) ?? []).filter((c) => c.kind === "availabilityDays").map((c) => parseDays(c.value));
  return lists.length ? [1, 2, 3, 4, 5, 6, 7].filter((d) => lists.every((allowed) => allowed.includes(d))) : undefined;
}

function bookableWeekday(ref: string, isoDay: number, today: DateKey): boolean {
  return bookableWeekdays(ref, today)?.includes(isoDay) ?? true;
}

/** Whether a patient's availability, as the branch knew it when booking, allows a visit on this day. */
function bookableDay(ref: string, day: DateKey, today: DateKey): boolean {
  return bookableWeekday(ref, isoWeekday(day), today);
}

/**
 * Whether a clinician's standing week has any day the patient can be seen. An
 * assistant who works only Tuesday and Thursday never alternates in for a
 * patient available Monday, Wednesday and Friday; the lead keeps those visits.
 */
function worksABookableDay(handle: string, ref: string, today: DateKey): boolean {
  const pattern = STANDING_PATTERNS[handle];
  if (!pattern || pattern.perDiem) return true;
  return pattern.weekdays.some((d) => bookableWeekday(ref, d, today));
}

export interface Placement {
  placed: number;
  dropped: number;
  droppedBy: Record<string, number>;
}

export type Schedule = ReturnType<typeof createSchedule>;

export function createSchedule(today: DateKey, nowDate: Date) {
  /** The day every beat the script calls "tomorrow" lands on. */
  const tomorrow = addWorkingDaysToKey(today, 1);
  let placement: Placement = { placed: 0, dropped: 0, droppedBy: {} };

  const thisMonday = weekStartKey(today);
  const PATTERN_DTSTART = addDaysToKey(thisMonday, -56);
  const BRANCH_BASED = new Set(["priya", "luis"]);

  /** Priya's orientation ramp: 6h this week, 4h next, 2h the week after (week 2 of 4 → ends Sunday of week 3). */
  const RAMP: { start: DateKey; end: DateKey; hours: number }[] = [
    { start: thisMonday, end: addDaysToKey(thisMonday, 6), hours: 6 },
    { start: addDaysToKey(thisMonday, 7), end: addDaysToKey(thisMonday, 13), hours: 4 },
    { start: addDaysToKey(thisMonday, 14), end: addDaysToKey(thisMonday, 20), hours: 2 },
  ];

  /**
   * Per-diem declared days come from the roster's working-day offsets from demo
   * day, not from a weekly pattern: a per diem declares particular days, and
   * Devon's call-out day is the one 5.2's coverage panel leans on. One rule per
   * declared day.
   */
  function perDiemDeclaredDays(handle: string): DateKey[] {
    const e = ROSTER_FIXTURE.find((x) => x.handle === handle)!;
    return (e.declaredDayOffsets ?? []).map((d) => addWorkingDaysToKey(today, d)).sort();
  }

  function rules(): Rule[] {
    const out: Rule[] = [];
    for (const e of ROSTER_FIXTURE) {
      const p = STANDING_PATTERNS[e.handle];
      if (p.perDiem) {
        for (const day of perDiemDeclaredDays(e.handle)) {
          const overflow = (p.overflowWeekdays ?? []).includes(isoWeekday(day));
          out.push({ handle: e.handle, type: "available", note: overflow ? "On-call (overflow shift)" : "Declared availability", start: `${p.start}:00`, end: `${p.end}:00`, freq: "weekly", dtstart: day, until: day, byweekday: [isoWeekday(day)], overflow, shiftCenter: BRANCH_BASED.has(e.handle) });
        }
        continue;
      }
      const plain = p.weekdays.filter((d) => !(p.overflowWeekdays ?? []).includes(d));
      const overflow = p.overflowWeekdays ?? [];
      if (plain.length) out.push({ handle: e.handle, type: "available", note: "Standing shift", start: `${p.start}:00`, end: `${p.end}:00`, freq: "weekly", dtstart: PATTERN_DTSTART, until: null, byweekday: plain, shiftCenter: BRANCH_BASED.has(e.handle) });
      if (overflow.length) out.push({ handle: e.handle, type: "available", note: "On-call (overflow shift)", start: `${p.start}:00`, end: `${p.end}:00`, freq: "weekly", dtstart: PATTERN_DTSTART, until: null, byweekday: overflow, overflow: true, shiftCenter: BRANCH_BASED.has(e.handle) });
      // The morning documentation catch-up every full-time clinician carries; Priya's ramp block holds her mornings instead.
      if (e.employment === "FT" && e.handle !== "priya") {
        out.push({ handle: e.handle, type: "calendar_block", note: "Documentation", start: `${DOCUMENTATION_BLOCK.start}:00`, end: `${DOCUMENTATION_BLOCK.end}:00`, freq: "weekly", dtstart: PATTERN_DTSTART, until: null, byweekday: plain.filter((d) => d <= 5), blockType: "break" });
      }
    }
    for (const r of RAMP) {
      out.push({ handle: "priya", type: "calendar_block", note: "Orientation ramp", start: "08:00:00", end: `${String(8 + r.hours).padStart(2, "0")}:00:00`, freq: "weekly", dtstart: r.start, until: r.end, byweekday: [1, 2, 3, 4, 5], blockType: "break" });
    }
    return out;
  }

  /** Mirrors availToShifts: one shift per (available rule × day); per-diem only within declared span. */
  function shiftsForHorizon(): SeedShift[] {
    const out: SeedShift[] = [];
    const days = dateKeyRange(addDaysToKey(today, -PIN_HORIZON_BEFORE), addDaysToKey(today, SHIFT_HORIZON_AFTER));
    for (const r of rules().filter((x) => x.type === "available")) {
      for (const day of days) {
        if (day < r.dtstart || (r.until && day > r.until)) continue;
        if (!r.byweekday.includes(isoWeekday(day))) continue;
        out.push({ handle: r.handle, day, startIso: dateKeyAt(day, r.start.slice(0, 5), TZ).toISOString(), endIso: dateKeyAt(day, r.end.slice(0, 5), TZ).toISOString(), overflow: !!r.overflow, shiftCenter: !!r.shiftCenter });
      }
    }
    return out;
  }


  function blocksForHorizon(): SeedBlock[] {
    const out: SeedBlock[] = [];
    const days = dateKeyRange(addDaysToKey(today, -PIN_HORIZON_BEFORE), addDaysToKey(today, SHIFT_HORIZON_AFTER));
    for (const r of rules().filter((x) => x.type === "calendar_block")) {
      for (const day of days) {
        if (day < r.dtstart || (r.until && day > r.until) || !r.byweekday.includes(isoWeekday(day))) continue;
        out.push({ handle: r.handle, day, note: r.note, startIso: dateKeyAt(day, r.start.slice(0, 5), TZ).toISOString(), endIso: dateKeyAt(day, r.end.slice(0, 5), TZ).toISOString(), blockType: r.blockType! });
      }
    }
    return out;
  }





  function seedPatients(): SeedPatient[] {
    const out: SeedPatient[] = [];
    // A retired slot still takes its index, so every other patient keeps its own.
    const named = PATIENT_SLOTS;
    named.forEach((p, i) => {
      if (RETIRED_PATIENT_REFS.has(p.ref)) return;
      const cast = CAST.find((c) => c.ref === p.ref);
      const socDaysAgo = cast?.socDaysAgo ?? (cast ? undefined : ((i * 7) % 47) + 3);
      out.push({
        ...p,
        mrn: `CMP-${String(1001 + i).padStart(6, "0")}`,
        socDate: socDaysAgo === undefined ? undefined : socDateBefore(today, socDaysAgo),
        team: cast?.team ?? teamFor(p.zip, p.caseType, i),
        requiredTraits: cast?.requiredTraits ?? (p.caseType === "woundCareSnPt" ? [TRAIT_WOUND_VAC] : []),
        stage: cast?.stage,
        orders: cast?.orders,
        realThrough: cast?.realThroughDays === undefined ? undefined : addDaysToKey(today, cast.realThroughDays),
        realVisitCap: cast?.realVisitCap,
        pinnedVisits: cast?.pinnedVisits,
        caseType: cast?.caseType ?? p.caseType,
        payer: cast?.payer ?? p.payer,
      });
    });
    // Generated census: surnames come from a pool disjoint from the cast's, so nobody shares a surname on screen.
    const used = new Set(out.map((p) => `${p.firstName} ${p.lastName}`));
    // The first name comes from the pool for the patient's sex; each sex takes every other k.
    const uniqueName = (k: number, sex: "F" | "M"): { firstName: string; lastName: string } => {
      const pool = firstNamesFor(sex);
      const nth = Math.floor(k / 2) * 7 + 3;
      let firstName = pool[nth % pool.length];
      let lastName = FILLER_LAST_NAMES[(k * 11 + 5) % FILLER_LAST_NAMES.length];
      let bump = 0;
      while (used.has(`${firstName} ${lastName}`)) {
        bump += 1;
        lastName = FILLER_LAST_NAMES[(k * 11 + 5 + bump) % FILLER_LAST_NAMES.length];
        if (bump > FILLER_LAST_NAMES.length) firstName = pool[(nth + bump) % pool.length];
      }
      used.add(`${firstName} ${lastName}`);
      return { firstName, lastName };
    };
    const extra = 52;
    const taken: Record<string, number> = {};
    for (let k = 0; k < extra; k++) {
      const territory = TERRITORY_MIX[k % TERRITORY_MIX.length];
      const bucket = UNUSED_ADDRESSES_BY_TERRITORY[territory] ?? [];
      const nth = (taken[territory] = (taken[territory] ?? 0) + 1) - 1;
      const addr = bucket[nth];
      // One household per patient: two patients at one address read on a clinician's
      // day as a visit with no drive between them. Better to stop than to reuse.
      if (!addr) throw new Error(`${territory} has ${bucket.length} unused addresses but the generated census needs ${nth + 1}. Add ${territory} addresses to fixtures/seedAddresses.ts.`);
      const sex = k % 2 ? "M" : "F";
      const { firstName, lastName } = uniqueName(k, sex);
      const caseType = CENSUS_MIX[k % CENSUS_MIX.length];
      const i = named.length + k;
      out.push({
        ref: `census-${String(i - 1).padStart(2, "0")}`,
        displayName: `${firstName[0]}. ${lastName}`,
        firstName,
        lastName,
        age: 66 + ((k * 13) % 27),
        sex,
        payer: k % 3 === 0 ? "MA" : "FFS",
        zip: addr[1],
        address1: addr[0],
        city: "Seattle",
        state: "WA",
        coordinates: addr[2],
        caseType,
        constraints: [],
        mrn: `CMP-${String(1001 + i).padStart(6, "0")}`,
        socDate: socDateBefore(today, ((k * 5) % 53) + 2),
        team: teamFor(addr[1], caseType, k),
        requiredTraits: caseType === "woundCareSnPt" ? [TRAIT_WOUND_VAC] : [],
      });
    }
    return out;
  }







  function buildVisits(patients: SeedPatient[]): SeedVisit[] {
    const shifts = shiftsForHorizon();
    const blocks = blocksForHorizon();
    const shiftOn = (handle: string, day: DateKey) => shifts.find((s) => s.handle === handle && s.day === day && !s.overflow);
    // Placement reads the documentation block as running to FIRST_VISIT_AT, so a one-hour block on the calendar
    // leaves the day's first visit where the two-hour block put it.
    const blocksOn = (handle: string, day: DateKey) =>
      blocks
        .filter((b) => b.handle === handle && b.day === day)
        .map((b) => (b.note === "Documentation" ? { ...b, endIso: dateKeyAt(day, FIRST_VISIT_AT, TZ).toISOString() } : b));
    const out: SeedVisit[] = [];
    const horizonStart = addDaysToKey(today, -HORIZON_BEFORE);
    const horizonEnd = addDaysToKey(today, HORIZON_AFTER);
    const pinStart = addDaysToKey(today, -PIN_HORIZON_BEFORE);

    // --- the plan every admitted patient carries ------------------------------
    interface Planned {
      patient: SeedPatient;
      visit: PlanVisit;
      used?: boolean;
    }
    const planned: Planned[] = [];
    for (const p of patients) {
      if (!p.socDate || isUnadmitted(p.stage)) continue;
      const seen = new Map<Discipline, number>();
      for (const v of expandEpisode({ ref: p.ref, socDate: p.socDate, caseType: p.caseType, orders: p.orders, requiredTraits: p.requiredTraits, availableWeekdays: bookableWeekdays(p.ref, today) }, DEFAULT_CONFIG).visits) {
        // Authorized units count treatment visits; the assessment is authorized on its own line.
        if (!DEFAULT_VISIT_TYPES[v.visitTypeKind].isAssessment) {
          const n = (seen.get(v.discipline) ?? 0) + 1;
          seen.set(v.discipline, n);
          const cap = p.realVisitCap?.[v.discipline];
          if (cap !== undefined && n > cap) continue; // past the authorized units: provisional, and provisional never reaches the tenant
        }
        if (v.targetDate < pinStart || v.targetDate > horizonEnd) continue;
        if (p.realThrough && v.targetDate > p.realThrough) continue;
        planned.push({ patient: p, visit: v });
      }
    }
    // Assessments get first pick of every day (they carry the 48h clock), then evaluations, then routine visits.
    // Within a tier the cast goes first: the script's patients sit on their plan days, and the generated
    // census fills around them, so a beat never rests on which background visit took a day first.
    const tier = (v: PlanVisit) => (DEFAULT_VISIT_TYPES[v.visitTypeKind].isAssessment ? 0 : v.visitTypeKind.endsWith("_EVAL") ? 1 : 2);
    const castRefs = new Set(CAST.map((m) => m.ref));
    const castRank = (p: SeedPatient) => (castRefs.has(p.ref) ? 0 : 1);
    planned.sort((a, b) => tier(a.visit) - tier(b.visit) || castRank(a.patient) - castRank(b.patient) || a.visit.targetDate.localeCompare(b.visit.targetDate));

    /** Mark the plan visit a hand-placed visit stands for, so the greedy fill does not place it twice. */
    /** Whether a day falls in the plan week a planned visit belongs to (its start of care's weekday, not Monday). */
    const inPlanWeek = (x: Planned, day: DateKey) => {
      const start = x.patient.socDate ? addDaysToKey(x.patient.socDate, x.visit.weekIndex * 7) : weekStartKey(x.visit.targetDate);
      return day >= start && day <= addDaysToKey(start, 6);
    };
    const plannedById = new Map(planned.map((x) => [x.visit.id, x]));
    /** A hand-placed visit delivers the plan visit of its own plan week, or none: never one from another week. */
    const consume = (ref: string, discipline: Discipline, day: DateKey, kind?: VisitTypeKind) => {
      const matches = (x: Planned) => !x.used && x.patient.ref === ref && (kind ? x.visit.visitTypeKind === kind : x.visit.discipline === discipline);
      const hit = planned.find((x) => matches(x) && inPlanWeek(x, day));
      if (hit) hit.used = true;
      return hit?.visit.id;
    };

    /** Intervals a clinician's day already carries: blocks, visits placed so far, and Marcus's reserved morning on the next working day. */
    const busyOn = (handle: string, day: DateKey): { start: number; end: number }[] => {
      const items = blocksOn(handle, day).map((b) => ({ start: new Date(b.startIso).getTime(), end: new Date(b.endIso).getTime() }));
      for (const v of out) {
        if (v.handle !== handle || v.day !== day) continue;
        items.push({ start: new Date(v.startIso).getTime() - MIN_DRIVE * 60_000, end: new Date(v.endIso).getTime() + MIN_DRIVE * 60_000 });
      }
      if (handle === "marcus" && day === tomorrow) {
        // Okafor's hold converts into a start of care here in 1.4; nothing else may take the slot.
        items.push({ start: dateKeyAt(day, MARCUS_NEXT_DAY_SOC.start, TZ).getTime(), end: dateKeyAt(day, MARCUS_NEXT_DAY_SOC.end, TZ).getTime() });
      }
      return items.sort((a, b) => a.start - b.start);
    };

    const weekPoints = new Map<string, number>();
    const pointsIn = (handle: string, day: DateKey) => weekPoints.get(`${handle}|${weekStartKey(day)}`) ?? 0;

    const place = (patient: SeedPatient, handle: string, kind: VisitTypeKind, day: DateKey, start: Date, planVisitId?: string): SeedVisit => {
      const end = new Date(start.getTime() + DEFAULT_VISIT_TYPES[kind].plannedMinutes * 60_000);
      const visit: SeedVisit = { patient, handle, kind, day, startIso: start.toISOString(), endIso: end.toISOString(), startDrive: 0, interDrive: 0, endDrive: 0, status: "scheduled", planVisitId };
      out.push(visit);
      weekPoints.set(`${handle}|${weekStartKey(day)}`, pointsIn(handle, day) + DEFAULT_VISIT_TYPES[kind].points);
      return visit;
    };

    /** The first start at or after `from` that clears the blocks and visits already on that day. */
    const freeStart = (handle: string, day: DateKey, from: Date, minutes: number): Date | undefined => {
      let s = from;
      for (let guard = 0; guard < 16; guard++) {
        const hit = busyOn(handle, day).find((iv) => s.getTime() < iv.end && s.getTime() + minutes * 60_000 > iv.start);
        if (!hit) return s;
        s = new Date(hit.end);
      }
      return undefined;
    };

    // --- 1. the visits the script places by hand ------------------------------
    for (const p of patients) {
      for (const pin of p.pinnedVisits ?? []) {
        const handle = pin.handle ?? p.team[disciplineOf(pin.kind) as keyof CastMember["team"]] ?? p.team.RN;
        if (!handle) continue;
        const pinDay = addWorkingDaysToKey(today, pin.dayOffset);
        const days = pin.everyWeek
          ? dateKeyRange(pinStart, horizonEnd).filter((d) => isoWeekday(d) === isoWeekday(pinDay))
          : [addDaysToKey(pinDay, (pin.weekOffset ?? 0) * 7)];
        for (const day of days) {
          if (day < pinStart || day > horizonEnd) continue;
          if (p.socDate && day < p.socDate) continue; // nothing before the start of care
          if (p.realThrough && day > p.realThrough) continue;
          if (!shiftOn(handle, day)) continue; // a day off: the script's pattern skips it, as it would in life
          if (out.some((v) => v.patient.ref === p.ref && v.day === day)) continue;
          place(p, handle, pin.kind, day, dateKeyAt(day, pin.start, TZ), consume(p.ref, disciplineOf(pin.kind), day, pin.kind));
        }
      }
    }

    // --- 2. Dana's week, laid out by hand -------------------------------------
    const danaRoutine = (day: DateKey, start: Date): Planned | undefined => {
      // Dana's own patients' nursing visits, the weeks the LPN alternates in included: a slot on her
      // hand-laid day takes a visit from its own plan week before it takes nobody, and only a patient
      // whose hours the slot fits.
      const pool = planned.filter(
        (x) =>
          !x.used &&
          x.visit.visitTypeKind === "SN_ROUTINE" &&
          (assignee(x.patient, x.visit, today) === "dana" || x.patient.team.RN === "dana") &&
          !out.some((v) => v.patient.ref === x.patient.ref && v.day === day) &&
          insidePatientWindow(x.patient.ref, today, day, start),
      );
      const inWeek = pool.filter((x) => inPlanWeek(x, day));
      // Her own territory first: a hand-laid day an hour apart has room for a drive inside North,
      // and a crossing pushes every later visit — on 2026-10-12 it pushed today's last one past 17:00.
      const home = supplyTerritoryOf("dana");
      const ranked = inWeek.sort(
        (a, b) => Number(territoryOf(a.patient) !== home) - Number(territoryOf(b.patient) !== home) || Math.abs(daysBetweenKeys(day, a.visit.targetDate)) - Math.abs(daysBetweenKeys(day, b.visit.targetDate)),
      );
      return ranked[0];
    };
    /** The days Dana's hand-laid fixture owns; the greedy fill leaves exactly these alone. */
    const danaFixtureDays = new Set<DateKey>();
    for (const danaDay of DANA_WEEK) {
      const day = addWorkingDaysToKey(today, danaDay.dayOffset);
      if (!shiftOn("dana", day)) continue; // a day off: the fixture's shape yields to her real calendar
      danaFixtureDays.add(day);
      for (const slot of danaDay.slots) {
        if (slot.ref) {
          const patient = patients.find((p) => p.ref === slot.ref);
          if (!patient || out.some((v) => v.patient.ref === slot.ref && v.day === day)) continue;
          place(patient, "dana", slot.kind, day, dateKeyAt(day, slot.start, TZ), consume(slot.ref, disciplineOf(slot.kind), day, slot.kind));
          continue;
        }
        const next = danaRoutine(day, dateKeyAt(day, slot.start, TZ));
        if (!next) continue;
        next.used = true;
        place(next.patient, "dana", slot.kind, day, dateKeyAt(day, slot.start, TZ), next.visit.id);
      }
    }

    // --- 3. greedy fill for everyone else -------------------------------------
    /**
     * Points a clinician's week may carry: their weekly target, so the branch reads
     * as placed rather than saturated, and the leadership view has open capacity to
     * talk about.
     */
    const weeklyCap = (handle: string): number => {
      const e = ROSTER_FIXTURE.find((x) => x.handle === handle)!;
      return e.employment === "perDiem" ? e.ceilingPoints : e.targetPoints;
    };

    /** Booked + drive minutes a clinician-day may carry. */
    const dayCap = (handle: string, shiftMinutes: number): number => {
      const e = ROSTER_FIXTURE.find((x) => x.handle === handle)!;
      const share = e.employment === "FT" ? 0.79 : e.employment === "PT" ? 0.82 : 0.6;
      return Math.min(shiftMinutes - 20, Math.round(shiftMinutes * share));
    };
    const loads = new Map<string, number>();
    const cursors = new Map<string, Date>();
    /**
     * The last patient placed on a clinician-day, so the next one is spaced by the
     * drive the seed is about to **write** rather than by the flat `MIN_DRIVE`
     * floor. `busyOn` pads a placed visit by `MIN_DRIVE` either side and
     * `interDrive` writes the leg's drive — two numbers out of the same loop that
     * once never agreed, so a crossing day was seeded with its drive bands running
     * back into the visit before them. The `drives that fit` calibration line is
     * the proof either way. The drive is the real one where the table has it
     * (Pete, 2026-10-12: placement uses the real drives too), the estimate
     * otherwise; `MIN_DRIVE` stays the floor, so a two-minute hop between
     * neighbours does not pack visits back to back.
     */
    const lastPatient = new Map<string, SeedPatient>();
    /** The gap a visit needs after the one before it: the drive `interDrive` will carry, at least `MIN_DRIVE`. */
    const gapAfter = (prev: SeedPatient | undefined, next: SeedPatient) =>
      prev === undefined ? 0 : Math.max(MIN_DRIVE, drive(prev.ref, next.ref, estimateDriveMinutes(territoryOf(prev), territoryOf(next)) - 8));
    let dropped = 0;
    const droppedBy: Record<string, number> = {};

    for (const entry of planned) {
      if (entry.used) continue;
      const { patient, visit } = entry;
      if (visit.targetDate < horizonStart) continue; // history is only what the script pins
      const handle = assignee(patient, visit, today);
      if (!handle) continue;
      const minutes = DEFAULT_VISIT_TYPES[visit.visitTypeKind].plannedMinutes;
      const points = DEFAULT_VISIT_TYPES[visit.visitTypeKind].points;
      let placed = false;
      // The visit stays inside its own plan week: its target day, then the later days of that
      // week, then the earlier ones. Spilling into the next week put a week's visits in the
      // week after it, so a card read one visit short and the next week one over.
      const planWeekStart = patient.socDate ? addDaysToKey(patient.socDate, visit.weekIndex * 7) : visit.targetDate;
      const planWeek = dateKeyRange(planWeekStart, addDaysToKey(planWeekStart, 6));
      const candidates = [...planWeek.filter((d) => d >= visit.targetDate), ...planWeek.filter((d) => d < visit.targetDate).reverse()];
      for (const day of candidates) {
        if (placed) break;
        if (day > horizonEnd || day < horizonStart) continue;
        if (handle === "dana" && danaFixtureDays.has(day)) continue; // those days are the fixture's, exactly
        if (!bookableDay(patient.ref, day, today)) continue; // booked around the availability the welcome call captured
        if (pointsIn(handle, day) + points > weeklyCap(handle)) continue;
        const shift = shiftOn(handle, day);
        if (!shift) continue;
        const shiftStart = new Date(shift.startIso);
        const shiftEnd = new Date(shift.endIso);
        const key = `${handle}|${day}`;
        // One patient, one visit, one day, one clinician. Nineteen days across the
        // horizon carried the same patient twice because the week was tight and the
        // fill took the first day with room; a prospect reads that as an error even
        // where a twice-daily wound visit would be legitimate. Dana's own fill has
        // had this guard all along. Nor two of the same kind on one day from two
        // clinicians (an RN and the LPN alternating in): nursing and therapy on one
        // day is ordinary, two nursing visits is not, and the store binds a plan
        // visit to the tenant's by patient, kind and day.
        if (out.some((v) => v.day === day && v.patient.ref === patient.ref && (v.handle === handle || v.kind === visit.visitTypeKind))) continue;
        const gap = gapAfter(lastPatient.get(key), patient);
        const cursor = cursors.get(key);
        // A patient who gave the branch hours is seen inside them: never earlier than their window
        // allows, and not at all on a day whose free time falls outside it.
        const window = bookableWindow(patient.ref, today);
        const earliest = window ? Math.max(shiftStart.getTime(), dateKeyAt(day, "00:00", TZ).getTime() + (window[0] + ARRIVAL_WINDOW_BEFORE_MINUTES) * 60_000) : shiftStart.getTime();
        const start = freeStart(handle, day, new Date(Math.max(cursor ? cursor.getTime() + gap * 60_000 : 0, earliest)), minutes);
        if (!start || !insidePatientWindow(patient.ref, today, day, start)) continue;
        const end = new Date(start.getTime() + minutes * 60_000);
        const homeDrive = drive(patient.ref, `@${handle}`, estimateDriveMinutes(supplyTerritoryOf(handle), territoryOf(patient)));
        if (end.getTime() + homeDrive * 60_000 > shiftEnd.getTime()) continue; // the day is full; try the next working day
        const loadAfter = (loads.get(key) ?? 0) + minutes + Math.max(gap, MIN_DRIVE);
        if (loadAfter + homeDrive > dayCap(handle, (shiftEnd.getTime() - shiftStart.getTime()) / 60_000)) continue;
        loads.set(key, loadAfter);
        cursors.set(key, end);
        lastPatient.set(key, patient);
        place(patient, handle, visit.visitTypeKind, day, start, visit.id);
        entry.used = true;
        placed = true;
      }
      if (!placed) {
        dropped += 1;
        droppedBy[visit.discipline] = (droppedBy[visit.discipline] ?? 0) + 1;
      }
    }

    placement = { placed: out.length, dropped, droppedBy };

    // --- 4b. book around the availability the welcome calls captured ----------
    // A visit that landed on a day the patient's availability rules out trades
    // patients with the same clinician's same kind of visit a day or two away in
    // the same week, where each patient can be seen. Slots, clinicians and points
    // stay exactly where the fill put them, so no other visit moves.
    for (const v of out) {
      if (bookableDay(v.patient.ref, v.day, today)) continue;
      const partner = out.find(
        (w) =>
          w.handle === v.handle &&
          w.kind === v.kind &&
          w.patient.ref !== v.patient.ref &&
          Math.abs(daysBetweenKeys(v.day, w.day)) <= 2 &&
          (!v.planVisitId || inPlanWeek(plannedById.get(v.planVisitId)!, w.day)) &&
          (!w.planVisitId || inPlanWeek(plannedById.get(w.planVisitId)!, v.day)) &&
          bookableDay(v.patient.ref, w.day, today) &&
          bookableDay(w.patient.ref, v.day, today) &&
          !out.some((x) => x !== w && x.patient.ref === v.patient.ref && x.day === w.day) &&
          !out.some((x) => x !== v && x.patient.ref === w.patient.ref && x.day === v.day),
      );
      if (!partner) continue;
      [v.patient, partner.patient] = [partner.patient, v.patient];
      [v.planVisitId, partner.planVisitId] = [partner.planVisitId, v.planVisitId];
    }

    // --- 5. drive times, per clinician-day in time order ----------------------
    // Drive belongs to the stop it *arrives at*, which is the product's own model
    // (`apps/service/services/updateDriveTimes.ts`, and `engine/dayAxis.ts` draws it
    // that way): `start_of_shift_drive_time` on the first stop of the day,
    // `inter_visit_drive_time` on every later one — the leg from the stop before it —
    // and `end_of_shift_drive_time` on the last, the drive home. A leg in the drive
    // table carries its real minutes (`drive`), as the fill above spaced by them;
    // step 6 pushes a hand-laid visit where the real drive is longer than its gap.
    const byDay = new Map<string, SeedVisit[]>();
    for (const v of out) byDay.set(`${v.handle}|${v.day}`, [...(byDay.get(`${v.handle}|${v.day}`) ?? []), v]);
    for (const [key, vs] of byDay) {
      const handle = key.split("|")[0];
      const home = supplyTerritoryOf(handle);
      vs.sort((a, b) => a.startIso.localeCompare(b.startIso));
      vs.forEach((v, i) => {
        v.startDrive = i === 0 ? drive(`@${handle}`, v.patient.ref, estimateDriveMinutes(home, territoryOf(v.patient))) : 0;
        v.interDrive = i > 0 ? drive(vs[i - 1].patient.ref, v.patient.ref, Math.max(MIN_DRIVE, estimateDriveMinutes(territoryOf(vs[i - 1].patient), territoryOf(v.patient)) - 8)) : 0;
        v.endDrive = i === vs.length - 1 ? drive(v.patient.ref, `@${handle}`, estimateDriveMinutes(territoryOf(v.patient), home)) : 0;
      });
    }
    // --- 6. the drive has to fit before the visit ----------------------------
    // Pete's rule, 2026-09-22: nothing overlaps by default, and a drive band lying
    // across the previous visit is an overlap to anyone in the room. The greedy
    // fill now spaces for the drive it writes, but the **hand-pinned** days are
    // placed before it and still carried gaps narrower than the crossing they
    // priced — eighteen of them on the 09-23 generation, ten on Dana's five demo
    // days. The first stop of a day never moves; every later one is pushed to
    // where the drive in front of it can fit, and the push cascades in time order.
    // A push never takes a visit past the end of the clinician's shift: one that
    // would is left unplaced instead, the way the fill leaves a visit with no room
    // (2026-10-12, a 16:15 visit pushed to 16:16–17:01).
    const overrun = new Set<SeedVisit>();
    for (const [key, vs] of byDay) {
      const [handle, day] = key.split("|");
      const shiftEnd = shiftOn(handle, day)?.endIso;
      for (let i = 1; i < vs.length; i += 1) {
        const needed = new Date(vs[i - 1].endIso).getTime() + vs[i].interDrive * 60_000;
        const start = new Date(vs[i].startIso).getTime();
        if (start >= needed) continue;
        const duration = new Date(vs[i].endIso).getTime() - start;
        if (shiftEnd && needed + duration > new Date(shiftEnd).getTime()) {
          for (const late of vs.slice(i)) overrun.add(late);
          break;
        }
        vs[i].startIso = new Date(needed).toISOString();
        vs[i].endIso = new Date(needed + duration).toISOString();
      }
      const kept = vs.filter((v) => !overrun.has(v));
      const last = kept[kept.length - 1];
      if (last && kept.length < vs.length) last.endDrive = drive(last.patient.ref, `@${handle}`, estimateDriveMinutes(territoryOf(last.patient), supplyTerritoryOf(handle)));
    }
    if (overrun.size) {
      placement = { ...placement, placed: placement.placed - overrun.size, dropped: placement.dropped + overrun.size };
      return out.filter((v) => !overrun.has(v)).sort((a, b) => a.startIso.localeCompare(b.startIso));
    }

    return out.sort((a, b) => a.startIso.localeCompare(b.startIso));
  }
  const patients = seedPatients();
  const visits = buildVisits(patients);
  // Statuses as of the demo time, after every push: complete, in progress just
  // after the start, arrived at it, en route within the drive before it. Dana's
  // are only done or not: the phone in 4.1 starts her next visit by hand, and
  // already being en route to something there is awkward (Pete, 2026-10-13).
  for (const v of visits) {
    const status = demoVisitStatus({ startTime: new Date(v.startIso), endTime: new Date(v.endIso), driveTime: Math.max(v.startDrive, v.interDrive) }, nowDate);
    v.status = v.handle === "dana" && status !== "complete" ? "scheduled" : status;
  }
  return { today, thisMonday, tomorrow, rules: rules(), shifts: shiftsForHorizon(), blocks: blocksForHorizon(), patients, visits, placement };
}
